// Finance data access. Every write goes through an admin-checked database
// function (migrations/005_payroll_bonus.sql); RLS limits what each user reads.
import { supabase } from "../supabase";
import { optimizedForUpload } from "../lib/uploadCompression";
import { storeFile, isR2 } from "../lib/fileStore";

export const PROOF_BUCKET = "salary-screenshots";

function unwrap({ data, error }) {
    if (error) throw new Error(error.message || "Request failed");
    return data;
}

export const getSalaryHistory = async () => unwrap(await supabase.from("salary_history").select("*").order("effective_from"));

export const setStaffSalary = async (staffId, monthlySalary, effectiveFrom, reason) =>
    unwrap(await supabase.rpc("set_staff_salary", { p_staff_id: staffId, p_monthly_salary: monthlySalary, p_effective_from: effectiveFrom, p_reason: reason || null }));

export const setPayrollSettings = async (staffId, { status, endDate, attendanceAffectsPayroll, isTest, reason }) =>
    unwrap(await supabase.rpc("set_payroll_settings", {
        p_staff_id: staffId, p_employment_status: status, p_employment_end_date: endDate || null,
        p_attendance_affects_payroll: attendanceAffectsPayroll, p_is_test_account: isTest, p_reason: reason,
    }));
export const getPayrollAudit = async (staffId) =>
    unwrap(await supabase.from("staff_payroll_audit").select("*").eq("staff_id", staffId).order("changed_at", { ascending: false }));

export const confirmSalaryHistory = async (id) => unwrap(await supabase.rpc("confirm_salary_history", { p_id: id }));
export const deleteSalaryHistory = async (id) => unwrap(await supabase.rpc("delete_salary_history", { p_id: id }));

export const previewSalaryMonth = async (staffId, year, month) =>
    unwrap(await supabase.rpc("preview_salary_month", { p_staff_id: staffId, p_year: year, p_month: month }));

export const finalizeSalaryPayment = async ({ staffId, year, month, amount, paidAt, method, txn, proof, notes }) =>
    unwrap(await supabase.rpc("finalize_salary_payment", {
        p_staff_id: staffId, p_year: year, p_month: month, p_paid_amount: amount, p_paid_at: paidAt,
        p_payment_method: method, p_txn_number: txn || null, p_proof: proof || null, p_note: notes || null,
    }));

export const markBonusPaid = async ({ bonusId, amount, paidAt, method, txn, proof, notes }) =>
    unwrap(await supabase.rpc("mark_bonus_paid", {
        p_bonus_id: bonusId, p_paid_amount: amount, p_paid_at: paidAt, p_payment_method: method,
        p_txn_number: txn || null, p_proof: proof || null, p_note: notes || null,
    }));

export const addBonus = async (row) => unwrap(await supabase.from("bonuses").insert(row).select());
export const updateBonus = async (id, row) => unwrap(await supabase.from("bonuses").update(row).eq("id", id).select());
export const deleteBonus = async (id) => unwrap(await supabase.from("bonuses").delete().eq("id", id));
export const addNotification = async (n) => unwrap(await supabase.from("notifications").insert(n));

// Private proof upload → returns the reference stored on the record: an "r2:" reference when R2 uploads are switched on
// (registered by the database when the payment row is saved), otherwise the object path in the bucket, as before.
export async function uploadProof(chosen, prefix) {
    if (!chosen) return null;
    const file = await optimizedForUpload(chosen);   // optimised proof image (or the original)
    const ext = (file.name.split(".").pop() || "bin").toLowerCase().replace(/[^a-z0-9]/g, "");
    try {
        const { ref } = await storeFile({ target: prefix.startsWith("bonus_") ? "payroll.bonus_proof" : "payroll.salary_proof", blob: file,
            fileName: file.name, mime: file.type || "application/octet-stream",
            legacy: { bucket: PROOF_BUCKET, path: `${prefix}_${Date.now()}.${ext}`, contentType: file.type || undefined } });
        return ref;
    } catch (e) { throw new Error(`Proof upload failed: ${e.message.replace(/^Upload failed: /, "")}`); }
}
// Clean-up after a failed save. An unregistered R2 upload is never deleted from the app: it is listed for a separately
// approved clean-up (storage_r2_open_uploads).
export async function removeProof(path) {
    if (path && !isR2(path)) await supabase.storage.from(PROOF_BUCKET).remove([path]).catch(() => {});
}
